import { ChevronDown } from "lucide-react";
import { useState, type FC } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  formatDay,
  yearStart,
  yearTicks,
  type Segment,
  type SegmentKind,
  type Tile,
  type TimelineView
} from "./timelineModel";

const SEGMENT_CLASS: Record<SegmentKind, string> = {
  status: "border-primary/25 bg-primary/10 text-primary",
  none: "border-dashed border-danger bg-danger-soft text-danger",
  in: "border-success-line bg-success-soft text-success",
  out: "border-border bg-muted text-muted-foreground",
  i94: "border-warn-line bg-warn-soft text-warn",
  doc: "border-border bg-fill-1 text-foreground"
};

const LEGEND: Array<{ label: string; className: string }> = [
  { label: "Status", className: "bg-primary" },
  { label: "In the U.S.", className: "bg-success" },
  { label: "Outside", className: "bg-muted-foreground" },
  { label: "No status", className: "bg-danger" }
];

const TILE_TONE: Record<NonNullable<Tile["tone"]>, string> = {
  red: "border-danger-line bg-danger-soft [&_[data-value]]:text-danger",
  amber: "border-warn-line bg-warn-soft [&_[data-value]]:text-warn"
};

const SummaryTile: FC<{ tile: Tile; testId: string }> = ({ tile, testId }) => (
  <div
    className={cn(
      "flex min-w-0 flex-col gap-0.5 rounded-md border border-border bg-background px-3 py-2.5",
      tile.tone && TILE_TONE[tile.tone]
    )}
    data-testid={testId}
  >
    <span className="text-xs text-muted-foreground">{tile.label}</span>
    <span className="truncate text-sm font-semibold" data-value>
      {tile.value}
    </span>
    {tile.sub ? (
      <span className="line-clamp-2 text-xs text-muted-foreground">
        {tile.sub}
      </span>
    ) : null}
  </div>
);

type Scale = (day: number) => number;

const Bar: FC<{ seg: Segment; x: Scale }> = ({ seg, x }) => {
  const left = Math.max(0, x(seg.from));
  const right = Math.min(100, x(seg.to));
  if (right <= left) return null;
  const range = `${formatDay(seg.from)} – ${
    seg.open ? "no end date" : formatDay(seg.to - 1)
  }`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "absolute top-1 flex h-6 items-center overflow-hidden rounded-md border px-1.5 text-xs font-medium",
            SEGMENT_CLASS[seg.kind],
            seg.past && seg.kind !== "none" && "opacity-80",
            seg.open && "rounded-r-none border-r-0"
          )}
          style={{ left: `${left}%`, width: `${right - left}%` }}
          data-kind={seg.kind}
        >
          <span className="truncate">{seg.label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="font-medium">{seg.label}</div>
        <div className="text-xs opacity-80">{range}</div>
      </TooltipContent>
    </Tooltip>
  );
};

const Lane: FC<{
  label: string;
  segments: Segment[];
  x: Scale;
  empty: string;
}> = ({ label, segments, x, empty }) => (
  <div className="grid grid-cols-[96px_minmax(0,1fr)] items-center border-b border-line-2 last:border-b-0 sm:grid-cols-[112px_minmax(0,1fr)]">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <span className="relative h-8">
      {segments.length === 0 ? (
        <span className="absolute inset-y-0 left-0 flex items-center text-xs text-fg-3">
          {empty}
        </span>
      ) : (
        segments.map((s) => (
          <Bar key={`${s.kind}-${s.from}-${s.to}`} seg={s} x={x} />
        ))
      )}
    </span>
  </div>
);

const COLLAPSED_KEY = "clientProfile.immigrationTimeline.collapsed";

const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

export const ImmigrationOverview: FC<{ view: TimelineView }> = ({ view }) => {
  const { window: range, lanes, today, tiles } = view;
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      // Private mode: the choice just isn't remembered.
    }
  };
  const span = range.to - range.from;
  const x: Scale = (day) => ((day - range.from) / span) * 100;
  const todayX = x(today);
  return (
    <div className="flex flex-col gap-3" data-testid="immigration-overview">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-[1.1fr_1fr_1fr_1.4fr]">
        {tiles.map((t, i) => (
          <SummaryTile
            key={t.label}
            tile={t}
            testId={`immigration-tile-${i}`}
          />
        ))}
      </div>
      <section
        className="rounded-lg border border-border bg-background"
        aria-label="Immigration timeline"
      >
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
          <h3 className="text-sm font-medium">Timeline</h3>
          <span className="flex flex-wrap items-center gap-3">
            {collapsed
              ? null
              : LEGEND.map((l) => (
                  <span
                    key={l.label}
                    className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex"
                  >
                    <span className={cn("size-2 rounded-xs", l.className)} />
                    {l.label}
                  </span>
                ))}
            <Button
              type="button"
              size="xs"
              variant="ghost"
              aria-expanded={!collapsed}
              onClick={toggle}
            >
              <ChevronDown
                className={cn(
                  "transition-transform",
                  collapsed && "-rotate-90"
                )}
              />
              {collapsed ? "Show" : "Hide"}
            </Button>
          </span>
        </header>
        {collapsed ? null : (
          <div className="overflow-x-auto px-3 pb-1">
            <div className="relative min-w-140">
              <div className="grid h-6 grid-cols-[96px_minmax(0,1fr)] items-end sm:grid-cols-[112px_minmax(0,1fr)]">
                <span />
                <span className="relative h-6">
                  {yearTicks(range).map((y) => (
                    <span
                      key={y}
                      className="absolute bottom-1 text-xs tabular-nums text-fg-3"
                      style={{ left: `${x(yearStart(y))}%` }}
                    >
                      {y}
                    </span>
                  ))}
                </span>
              </div>
              <Lane
                label="Status"
                segments={lanes.status}
                x={x}
                empty="No statuses recorded"
              />
              <Lane
                label="Where they were"
                segments={lanes.presence}
                x={x}
                empty="No entries or exits recorded"
              />
              <Lane
                label="I-94"
                segments={lanes.i94}
                x={x}
                empty="No I-94 recorded"
              />
              <Lane
                label="Passport"
                segments={lanes.passport}
                x={x}
                empty="No passport recorded"
              />
              {todayX >= 0 && todayX <= 100 ? (
                <div
                  className="pointer-events-none absolute bottom-0 top-5 left-24 right-0 sm:left-28"
                  aria-hidden
                >
                  <span
                    className="absolute bottom-0 top-1 w-[1.5px] bg-primary"
                    style={{ left: `${todayX}%` }}
                  />
                  <span
                    className="absolute -top-5 -translate-x-1/2 text-xs font-semibold text-primary"
                    style={{ left: `${todayX}%` }}
                  >
                    Today
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
